/**
 * Bulk Import Dialog — 3-step flow:
 *   1. Download CSV template / export the current catalog + read instructions
 *   2. Upload filled CSV → bulk-create/update products
 *   3. Upload images (files or a whole folder) → names are matched to products
 *      by SKU / product id / slug / product name (with `-1`/`_2` series
 *      suffixes), then reviewed in a MAPPING TABLE where the admin can
 *      include/exclude files, re-assign products, reorder and pick the primary
 *      before anything uploads. The confirmed mapping — not the filename — is
 *      what the server applies.
 */

import { useState, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Download,
  Upload,
  FileSpreadsheet,
  FolderOpen,
  ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  Star,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { api, apiHelpers, type BulkImageMatchData, type BulkImageMatchProposal } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// ── Types ──

interface ImportRowError {
  row: number;
  field: string;
  message: string;
}

interface CsvImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: ImportRowError[];
  /** Informational messages (slug auto-dedupe etc.) — not failures. */
  notes?: string[];
  products: Array<{ name: string; slug: string; _id: string; _updated?: boolean }>;
}

/** One file in the image mapping table. */
interface MappingRow {
  file: File;
  /** object URL for the thumbnail (revoked on close) */
  previewUrl: string;
  include: boolean;
  productId: string | null;
  /** relative position within the product's new images */
  order: number;
  isPrimary: boolean;
  matchType: BulkImageMatchProposal['matchType'];
  ambiguous: boolean;
}

interface ImageUploadResult {
  uploaded: number;
  matched: number;
  unmatched: Array<{ filename: string; reason: string }>;
  details: Array<{ slug: string; imagesAdded: number }>;
}

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Component ──

export default function BulkImportDialog({ projectId, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const csvInputRef = useRef<HTMLInputElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);

  const folderInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState('guide');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [csvResult, setCsvResult] = useState<CsvImportResult | null>(null);
  const [imageResult, setImageResult] = useState<ImageUploadResult | null>(null);
  const [mapping, setMapping] = useState<MappingRow[]>([]);
  const [matchProducts, setMatchProducts] = useState<BulkImageMatchData['products']>([]);
  const [matching, setMatching] = useState(false);

  const basePath = `/projects/${projectId}/ecommerce`;

  // ── Reset on close ──
  function handleOpenChange(v: boolean) {
    if (!v) {
      setCsvFile(null);
      setImageFiles([]);
      setCsvResult(null);
      setImageResult(null);
      for (const row of mapping) URL.revokeObjectURL(row.previewUrl);
      setMapping([]);
      setMatchProducts([]);
      setActiveTab('guide');
    }
    onOpenChange(v);
  }

  /**
   * Build the mapping table for a fresh image selection: ask the server for
   * filename→product proposals, then seed a per-file editable row. Defaults:
   *   - matched, unambiguous files are INCLUDED
   *   - the first image of a product that has none becomes primary
   *   - unmatched/ambiguous files are excluded until the admin assigns them
   */
  async function selectImages(files: File[]) {
    for (const row of mapping) URL.revokeObjectURL(row.previewUrl);
    setImageResult(null);
    setImageFiles(files);
    if (files.length === 0) {
      setMapping([]);
      return;
    }
    setMatching(true);
    try {
      const res = await apiHelpers.matchBulkImageFilenames(
        projectId,
        files.map((f) => f.name)
      );
      const data = res.data.data as BulkImageMatchData;
      setMatchProducts(data.products);
      const imageCountByProduct = new Map(data.products.map((pr) => [pr.id, pr.imageCount]));
      const primaryAssigned = new Set<string>();

      const rows: MappingRow[] = files.map((file, i) => {
        const m = data.matches[i];
        const productId = m?.productId ?? null;
        let isPrimary = false;
        if (
          productId &&
          (imageCountByProduct.get(productId) || 0) === 0 &&
          !primaryAssigned.has(productId) &&
          (m.seriesIndex === 0 || m.seriesIndex === 1)
        ) {
          isPrimary = true;
          primaryAssigned.add(productId);
        }
        return {
          file,
          previewUrl: URL.createObjectURL(file),
          include: Boolean(productId),
          productId,
          order: m?.seriesIndex && m.seriesIndex > 0 ? m.seriesIndex : 500 + i,
          isPrimary,
          matchType: m?.matchType ?? null,
          ambiguous: Boolean(m?.ambiguous),
        };
      });
      setMapping(rows);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Could not match filenames');
      setMapping(
        files.map((file, i) => ({
          file,
          previewUrl: URL.createObjectURL(file),
          include: false,
          productId: null,
          order: 500 + i,
          isPrimary: false,
          matchType: null,
          ambiguous: false,
        }))
      );
    } finally {
      setMatching(false);
    }
  }

  function updateRow(index: number, patch: Partial<MappingRow>) {
    setMapping((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  /** Make row `index` the (only) primary among rows of the same product. */
  function setRowPrimary(index: number) {
    setMapping((rows) => {
      const target = rows[index];
      if (!target?.productId) return rows;
      return rows.map((row, i) =>
        row.productId === target.productId ? { ...row, isPrimary: i === index } : row
      );
    });
  }

  // ── Download template ──
  async function downloadTemplate() {
    try {
      const res = await api.get(`${basePath}/products/bulk/template`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  }

  // ── Export products ──
  const [exporting, setExporting] = useState(false);
  async function exportProducts() {
    setExporting(true);
    try {
      const res = await api.get(`${basePath}/products/bulk/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Products exported');
    } catch {
      toast.error('Failed to export products');
    } finally {
      setExporting(false);
    }
  }

  // ── CSV import mutation ──
  const csvMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`${basePath}/products/bulk/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
      return res.data.data as CsvImportResult;
    },
    onSuccess: (data) => {
      setCsvResult(data);
      queryClient.invalidateQueries({ queryKey: ['products', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-categories', projectId] });
      const parts: string[] = [];
      if (data.created > 0) parts.push(`${data.created} created`);
      if (data.updated > 0) parts.push(`${data.updated} updated`);
      if (parts.length > 0) {
        toast.success(`Products: ${parts.join(', ')}`);
        setActiveTab('images');
      }
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      if (data?.data?.errors?.length) {
        setCsvResult(data.data);
      } else {
        toast.error(data?.error?.message || 'Import failed');
      }
    },
  });

  // ── Direct-to-R2 image upload (presign → PUT to R2 → finalize) ──
  const [imageProgress, setImageProgress] = useState<string | null>(null);
  const [failedUploads, setFailedUploads] = useState<File[]>([]);
  const [showRetryDialog, setShowRetryDialog] = useState(false);

  const CONCURRENCY = 5; // parallel R2 PUT uploads

  /**
   * Upload files directly to R2 using presigned URLs.
   *
   * Presigns in chunks of CONCURRENCY and immediately uploads them,
   * so every URL is used within milliseconds of being generated — no 403 expiry.
   */
  async function uploadToR2(files: Array<{ file: File; productId?: string }>): Promise<{
    succeeded: Array<{ filename: string; key: string }>;
    failed: File[];
  }> {
    const succeeded: Array<{ filename: string; key: string }> = [];
    const failed: File[] = [];
    let completed = 0;
    const total = files.length;

    // Process files in chunks of CONCURRENCY: presign chunk → upload chunk in parallel
    for (let i = 0; i < files.length; i += CONCURRENCY) {
      const chunk = files.slice(i, i + CONCURRENCY);

      // Presign this chunk (URLs are fresh — used immediately)
      let urls: Array<{ filename: string; key: string; uploadUrl: string }> = [];
      try {
        const res = await api.post(`${basePath}/products/bulk/images/presign`, {
          files: chunk.map((f) => ({
            filename: f.file.name,
            contentType: f.file.type || 'application/octet-stream',
            ...(f.productId ? { productId: f.productId } : {}),
          })),
        });
        const data = res.data.data as {
          urls: Array<{ filename: string; key: string; uploadUrl: string }>;
          errors: Array<{ filename: string; reason: string }>;
        };
        urls = data.urls;
        // Files that failed presign validation
        for (const err of data.errors || []) {
          const f = chunk.find((c) => c.file.name === err.filename);
          if (f) failed.push(f.file);
        }
      } catch {
        // Entire presign batch failed — mark all as failed
        failed.push(...chunk.map((c) => c.file));
        completed += chunk.length;
        setImageProgress(`Uploading ${completed} of ${total}...`);
        continue;
      }

      // Upload this chunk in parallel (all URLs are < 1 second old)
      const chunkFileMap = new Map(chunk.map((f) => [f.file.name, f.file]));
      await Promise.allSettled(
        urls.map(async (item) => {
          const file = chunkFileMap.get(item.filename);
          if (!file) return;
          try {
            const putRes = await fetch(item.uploadUrl, {
              method: 'PUT',
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
              body: file,
            });
            if (!putRes.ok) throw new Error(`HTTP ${putRes.status}`);
            succeeded.push({ filename: item.filename, key: item.key });
          } catch {
            failed.push(file);
          } finally {
            completed++;
            setImageProgress(`Uploading ${completed} of ${total}...`);
          }
        })
      );
    }

    return { succeeded, failed };
  }

  const imageMutation = useMutation({
    mutationFn: async (
      rows: MappingRow[]
    ): Promise<{
      result: ImageUploadResult;
      failed: File[];
    }> => {
      const included = rows.filter((r) => r.include && r.productId);
      setImageProgress(`Preparing ${included.length} images...`);

      // Upload directly to R2 (productId lets presign accept any filename)
      const { succeeded, failed } = await uploadToR2(
        included.map((r) => ({ file: r.file, productId: r.productId! }))
      );

      // Finalize with the CONFIRMED mapping — the dialog, not the filename,
      // decides which product each file lands on.
      let result: ImageUploadResult = { uploaded: 0, matched: 0, unmatched: [], details: [] };

      if (succeeded.length > 0) {
        setImageProgress('Attaching images to products...');
        const byName = new Map(included.map((r) => [r.file.name, r]));
        const res = await api.post(`${basePath}/products/bulk/images/finalize`, {
          files: succeeded.map((f) => {
            const row = byName.get(f.filename)!;
            return {
              filename: f.filename,
              key: f.key,
              productId: row.productId,
              order: row.order,
              isPrimary: row.isPrimary,
            };
          }),
        });
        result = res.data.data as ImageUploadResult;
      }

      setImageProgress(null);
      return { result, failed };
    },
    onSuccess: ({ result, failed }) => {
      setImageResult(result);
      queryClient.invalidateQueries({ queryKey: ['products', projectId] });
      if (result.uploaded > 0) {
        toast.success(
          `${result.uploaded} image${result.uploaded > 1 ? 's' : ''} uploaded and matched`
        );
      }
      if (failed.length > 0) {
        setFailedUploads(failed);
        setShowRetryDialog(true);
      } else {
        setFailedUploads([]);
        setShowRetryDialog(false);
      }
    },
    onError: (err: any) => {
      setImageProgress(null);
      toast.error(err?.response?.data?.error?.message || 'Image upload failed');
    },
  });

  // ── Handlers ──

  function handleCsvSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setCsvResult(null);
    }
    e.target.value = '';
  }

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []).filter((f) =>
      /\.(jpg|jpeg|png|webp|gif|avif)$/i.test(f.name)
    );
    if (files.length > 0) void selectImages(files);
    e.target.value = '';
  }

  const handleCsvDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type.includes('csv'))) {
      setCsvFile(file);
      setCsvResult(null);
    } else {
      toast.error('Please drop a CSV file');
    }
  }, []);

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      /\.(jpg|jpeg|png|webp|gif|avif)$/i.test(f.name)
    );
    if (files.length > 0) void selectImages(files);
    else toast.error('Please drop image files (JPEG, PNG, WebP, GIF, AVIF)');
  };

  function preventDefault(e: React.DragEvent) {
    e.preventDefault();
  }

  // ── Render ──

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Products</DialogTitle>
          <DialogDescription>
            Import products from a CSV file and upload images in bulk.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="guide" className="text-xs sm:text-sm">
              <Info className="mr-1.5 h-3.5 w-3.5" />
              Guide
            </TabsTrigger>
            <TabsTrigger value="csv" className="text-xs sm:text-sm">
              <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
              Upload CSV
            </TabsTrigger>
            <TabsTrigger value="images" className="text-xs sm:text-sm">
              <ImageIcon className="mr-1.5 h-3.5 w-3.5" />
              Upload Images
            </TabsTrigger>
          </TabsList>

          {/* ─────────────── GUIDE TAB ─────────────── */}
          <TabsContent value="guide" className="space-y-4">
            {/* Step 1 */}
            <div className="space-y-2 rounded-lg border p-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                  1
                </span>
                Download template or export existing products
              </h3>
              <p className="text-muted-foreground text-sm">
                Start from the blank template for new products, or{' '}
                <strong>export your existing products</strong> to bulk-edit prices, stock,
                descriptions, etc. The exported CSV includes product IDs so changes are matched back
                automatically.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="mr-2 h-4 w-4" />
                  Blank Template
                </Button>
                <Button variant="outline" size="sm" onClick={exportProducts} disabled={exporting}>
                  {exporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Export Products
                </Button>
              </div>
            </div>

            {/* Step 2 */}
            <div className="space-y-2 rounded-lg border p-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                  2
                </span>
                Upload the filled CSV
              </h3>
              <p className="text-muted-foreground text-sm">
                Go to the <strong>Upload CSV</strong> tab and upload your CSV. Rows with an
                <strong> _id</strong> update existing products; rows without create new ones.
                Categories referenced by name are auto-created if they don't exist.
              </p>
              <div className="text-muted-foreground mt-1 space-y-1 text-xs">
                <p>
                  <strong>Required columns:</strong> name, description, price
                </p>
                <p>
                  <strong>Price:</strong> Enter in smallest currency unit (paise for INR, cents for
                  USD). E.g. 2999 for {'\u20b9'}29.99
                </p>
                <p>
                  <strong>Slug:</strong> Auto-generated from name if left blank
                </p>
                <p>
                  <strong>_id:</strong> Leave blank for new products. Pre-filled in exported CSVs
                  for updates.
                </p>
                <p>Max 500 products per import.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="space-y-3 rounded-lg border p-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                  3
                </span>
                Upload product images
              </h3>
              <p className="text-muted-foreground text-sm">
                After importing the CSV, go to the <strong>Upload Images</strong> tab. Name your
                image files using the product <strong>slug</strong> so they're automatically
                matched.
              </p>

              <div className="bg-muted/50 space-y-2 rounded-md p-3">
                <p className="text-sm font-medium">Image naming convention</p>
                <code className="bg-background block rounded px-2 py-1.5 font-mono text-xs">
                  {'{slug}_{number}.{ext}'}
                </code>
                <div className="text-muted-foreground grid gap-1 text-xs">
                  <p>
                    <code className="bg-background rounded px-1">blue-cotton-tshirt_1.jpg</code> —
                    Primary image
                  </p>
                  <p>
                    <code className="bg-background rounded px-1">blue-cotton-tshirt_2.jpg</code> —
                    Second image
                  </p>
                  <p>
                    <code className="bg-background rounded px-1">blue-cotton-tshirt_3.png</code> —
                    Third image
                  </p>
                </div>
                <ul className="text-muted-foreground mt-1 list-inside list-disc space-y-0.5 text-xs">
                  <li>
                    Image <strong>_1</strong> is always the primary/featured image
                  </li>
                  <li>Numbers determine display order</li>
                  <li>Supported: JPG, PNG, WebP, GIF, AVIF (max 10 MB each)</li>
                  <li>Max 20 images per product, 100 images per upload</li>
                </ul>
              </div>
            </div>

            <Button className="w-full" onClick={() => setActiveTab('csv')}>
              Get Started — Upload CSV
            </Button>
          </TabsContent>

          {/* ─────────────── CSV TAB ─────────────── */}
          <TabsContent value="csv" className="space-y-4">
            {/* Download template shortcut */}
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">Don't have the template yet?</p>
              <Button variant="link" size="sm" className="h-auto p-0" onClick={downloadTemplate}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download Template
              </Button>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleCsvDrop}
              onDragOver={preventDefault}
              onDragEnter={preventDefault}
              onClick={() => csvInputRef.current?.click()}
              className="hover:border-primary/50 hover:bg-muted/30 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors"
            >
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={handleCsvSelect}
                className="hidden"
              />
              <FileSpreadsheet className="text-muted-foreground mb-3 h-10 w-10" />
              {csvFile ? (
                <div className="text-center">
                  <p className="font-medium">{csvFile.name}</p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {(csvFile.size / 1024).toFixed(1)} KB — Click to change
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-medium">Drop your CSV file here</p>
                  <p className="text-muted-foreground mt-1 text-xs">or click to browse</p>
                </div>
              )}
            </div>

            {/* Import button */}
            {csvFile && !csvResult && (
              <Button
                className="w-full"
                onClick={() => csvMutation.mutate(csvFile)}
                disabled={csvMutation.isPending}
              >
                {csvMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Import Products
                  </>
                )}
              </Button>
            )}

            {/* Results */}
            {csvResult && <CsvResultPanel result={csvResult} />}

            {/* Next step after success */}
            {csvResult && csvResult.created > 0 && (
              <Button className="w-full" variant="outline" onClick={() => setActiveTab('images')}>
                <ImageIcon className="mr-2 h-4 w-4" />
                Continue — Upload Images
              </Button>
            )}

            {/* Retry on error */}
            {csvResult && csvResult.created === 0 && csvResult.errors.length > 0 && (
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  setCsvFile(null);
                  setCsvResult(null);
                }}
              >
                Fix CSV and Try Again
              </Button>
            )}
          </TabsContent>

          {/* ─────────────── IMAGES TAB ─────────────── */}
          <TabsContent value="images" className="space-y-4">
            {/* Naming reminder */}
            <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-sm dark:bg-blue-950/30">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
              <div className="text-blue-800 dark:text-blue-200">
                <p className="font-medium">How matching works</p>
                <p className="mt-0.5 text-xs">
                  Name each file after the product&apos;s <strong>SKU</strong>, <strong>ID</strong>,{' '}
                  <strong>slug</strong> or <strong>exact name</strong> — e.g.{' '}
                  <code className="rounded bg-blue-100 px-1 dark:bg-blue-900">SKU123.jpg</code>. For
                  several images of one product, add a series number:{' '}
                  <code className="rounded bg-blue-100 px-1 dark:bg-blue-900">SKU123-1.jpg</code>,{' '}
                  <code className="rounded bg-blue-100 px-1 dark:bg-blue-900">SKU123-2.jpg</code>.
                  You review and adjust every match below before anything uploads.
                </p>
              </div>
            </div>

            {/* Drop zone + folder picker */}
            <div className="flex gap-3">
              <div
                onDrop={handleImageDrop}
                onDragOver={preventDefault}
                onDragEnter={preventDefault}
                onClick={() => imgInputRef.current?.click()}
                className="hover:border-primary/50 hover:bg-muted/30 flex flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors"
              >
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <ImageIcon className="text-muted-foreground mb-2 h-8 w-8" />
                <div className="text-center">
                  <p className="text-sm font-medium">
                    {imageFiles.length > 0
                      ? `${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} selected — review the mapping below`
                      : 'Drop product images here'}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {imageFiles.length > 0 ? 'Click to change selection' : 'or click to browse'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => folderInputRef.current?.click()}
                className="hover:border-primary/50 hover:bg-muted/30 flex w-36 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 transition-colors"
              >
                {/* webkitdirectory: select an entire folder; subfolder paths are stripped server-side */}
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                />
                <FolderOpen className="text-muted-foreground mb-2 h-6 w-6" />
                <span className="text-xs font-medium">Select a folder</span>
              </button>
            </div>

            {matching && (
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Matching filenames to products…
              </p>
            )}

            {/* Mapping review table — the admin's final say before upload */}
            {mapping.length > 0 && !imageResult && !matching && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">
                      {mapping.filter((r) => r.include && r.productId).length}
                    </strong>{' '}
                    of {mapping.length} will upload
                    {mapping.some((r) => !r.productId) && (
                      <span className="ml-2 text-amber-600">
                        {mapping.filter((r) => !r.productId).length} unmatched — assign a product or
                        leave excluded
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() =>
                        setMapping((rows) =>
                          rows.map((r) => ({ ...r, include: Boolean(r.productId) }))
                        )
                      }
                    >
                      Select all matched
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() =>
                        setMapping((rows) => rows.map((r) => ({ ...r, include: false })))
                      }
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/60 sticky top-0">
                      <tr className="text-left">
                        <th className="w-8 p-2" />
                        <th className="w-12 p-2">Image</th>
                        <th className="p-2">File</th>
                        <th className="w-48 p-2">Product</th>
                        <th className="w-14 p-2" title="Position among this product's images">
                          Order
                        </th>
                        <th className="w-14 p-2" title="Primary image of the product">
                          Primary
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mapping.map((row, i) => (
                        <tr
                          key={row.file.name + i}
                          className={`border-t ${row.include ? '' : 'opacity-50'} ${
                            !row.productId ? 'bg-amber-50 dark:bg-amber-950/20' : ''
                          }`}
                        >
                          <td className="p-2">
                            <Checkbox
                              checked={row.include}
                              disabled={!row.productId}
                              onCheckedChange={(v) => updateRow(i, { include: v === true })}
                            />
                          </td>
                          <td className="p-2">
                            <img
                              src={row.previewUrl}
                              alt=""
                              className="h-9 w-9 rounded object-cover"
                            />
                          </td>
                          <td className="max-w-[160px] p-2">
                            <p className="truncate font-medium" title={row.file.name}>
                              {row.file.name}
                            </p>
                            <p className="text-muted-foreground">
                              {row.matchType
                                ? `matched by ${row.matchType.toUpperCase()}`
                                : row.ambiguous
                                  ? 'ambiguous — pick the product'
                                  : 'no match'}
                            </p>
                          </td>
                          <td className="p-2">
                            <select
                              className="border-input bg-background w-full rounded-md border px-2 py-1.5"
                              value={row.productId ?? ''}
                              onChange={(e) =>
                                updateRow(i, {
                                  productId: e.target.value || null,
                                  include: Boolean(e.target.value),
                                })
                              }
                            >
                              <option value="">— not assigned —</option>
                              {matchProducts.map((prod) => (
                                <option key={prod.id} value={prod.id}>
                                  {prod.name}
                                  {prod.sku ? ` (${prod.sku})` : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min={0}
                              className="border-input bg-background w-12 rounded-md border px-1.5 py-1.5"
                              value={row.order}
                              onChange={(e) =>
                                updateRow(i, { order: parseInt(e.target.value, 10) || 0 })
                              }
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              title="Make this the product's primary image"
                              onClick={() => setRowPrimary(i)}
                              disabled={!row.productId}
                              className="disabled:opacity-30"
                            >
                              <Star
                                className={`h-4 w-4 ${
                                  row.isPrimary
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-muted-foreground'
                                }`}
                              />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Upload button — applies the confirmed mapping */}
            {mapping.length > 0 && !imageResult && (
              <Button
                className="w-full"
                onClick={() => imageMutation.mutate(mapping)}
                disabled={
                  imageMutation.isPending ||
                  mapping.filter((r) => r.include && r.productId).length === 0
                }
              >
                {imageMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {imageProgress || 'Uploading…'}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload {mapping.filter((r) => r.include && r.productId).length} mapped image
                    {mapping.filter((r) => r.include && r.productId).length === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            )}

            {/* Results */}
            {imageResult && <ImageResultPanel result={imageResult} />}

            {/* Failed uploads — retry dialog */}
            {showRetryDialog && failedUploads.length > 0 && (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">
                      {failedUploads.length} image{failedUploads.length > 1 ? 's' : ''} failed to
                      upload
                    </p>
                    <ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto text-xs text-amber-700">
                      {failedUploads.map((f, i) => (
                        <li key={i}>
                          {f.name} ({(f.size / 1024).toFixed(0)} KB)
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowRetryDialog(false);
                      setFailedUploads([]);
                    }}
                  >
                    Skip
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowRetryDialog(false);
                      const failedNames = new Set(failedUploads.map((f) => f.name));
                      imageMutation.mutate(mapping.filter((r) => failedNames.has(r.file.name)));
                    }}
                    disabled={imageMutation.isPending}
                  >
                    {imageMutation.isPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Retry Failed
                  </Button>
                </div>
              </div>
            )}

            {/* Done */}
            {imageResult && imageResult.uploaded > 0 && !showRetryDialog && (
              <Button className="w-full" onClick={() => handleOpenChange(false)}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Done
              </Button>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components ──

function CsvResultPanel({ result }: { result: CsvImportResult }) {
  const hasErrors = result.errors.length > 0;
  const hasCreated = result.created > 0;
  const hasUpdated = result.updated > 0;

  return (
    <div className="space-y-3">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        {hasCreated && (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            {result.created} created
          </Badge>
        )}
        {hasUpdated && (
          <Badge variant="default" className="bg-blue-600">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            {result.updated} updated
          </Badge>
        )}
        {result.skipped > 0 && <Badge variant="secondary">{result.skipped} skipped</Badge>}
        {hasErrors && (
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-3 w-3" />
            {result.errors.length} error{result.errors.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Created products list */}
      {hasCreated && (
        <div className="rounded-md bg-green-50 p-3 dark:bg-green-950/30">
          <p className="mb-1 text-sm font-medium text-green-800 dark:text-green-200">
            Products created:
          </p>
          <div className="max-h-32 space-y-0.5 overflow-y-auto">
            {result.products
              .filter((p) => !p._updated)
              .map((p) => (
                <p key={p._id} className="text-xs text-green-700 dark:text-green-300">
                  {p.name} <span className="text-green-500 dark:text-green-400">({p.slug})</span>
                </p>
              ))}
          </div>
        </div>
      )}

      {/* Updated products list */}
      {hasUpdated && (
        <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-950/30">
          <p className="mb-1 text-sm font-medium text-blue-800 dark:text-blue-200">
            Products updated:
          </p>
          <div className="max-h-32 space-y-0.5 overflow-y-auto">
            {result.products
              .filter((p) => p._updated)
              .map((p) => (
                <p key={p._id} className="text-xs text-blue-700 dark:text-blue-300">
                  {p.name} <span className="text-blue-500 dark:text-blue-400">({p.slug})</span>
                </p>
              ))}
          </div>
        </div>
      )}

      {/* Informational notes (e.g. slug auto-dedupe) */}
      {(result.notes?.length ?? 0) > 0 && (
        <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-950/30">
          <p className="mb-1 text-sm font-medium text-blue-800 dark:text-blue-200">Notes:</p>
          <div className="max-h-24 space-y-0.5 overflow-y-auto">
            {result.notes!.map((n, i) => (
              <p key={i} className="text-xs text-blue-700 dark:text-blue-300">
                {n}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {hasErrors && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-950/30">
          <p className="mb-1 text-sm font-medium text-red-800 dark:text-red-200">Issues found:</p>
          <div className="max-h-40 space-y-0.5 overflow-y-auto">
            {result.errors.map((err, i) => (
              <p key={i} className="text-xs text-red-700 dark:text-red-300">
                <strong>Row {err.row}</strong> ({err.field}): {err.message}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImageResultPanel({ result }: { result: ImageUploadResult }) {
  return (
    <div className="space-y-3">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        {result.uploaded > 0 && (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            {result.uploaded} uploaded
          </Badge>
        )}
        {result.matched > 0 && (
          <Badge variant="secondary">
            {result.matched} product{result.matched > 1 ? 's' : ''} matched
          </Badge>
        )}
        {result.unmatched.length > 0 && (
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-3 w-3" />
            {result.unmatched.length} unmatched
          </Badge>
        )}
      </div>

      {/* Matched details */}
      {result.details.length > 0 && (
        <div className="rounded-md bg-green-50 p-3 dark:bg-green-950/30">
          <p className="mb-1 text-sm font-medium text-green-800 dark:text-green-200">
            Images matched to products:
          </p>
          <div className="max-h-32 space-y-0.5 overflow-y-auto">
            {result.details.map((d) => (
              <p key={d.slug} className="text-xs text-green-700 dark:text-green-300">
                <strong>{d.slug}</strong> — {d.imagesAdded} image
                {d.imagesAdded > 1 ? 's' : ''} added
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Unmatched */}
      {result.unmatched.length > 0 && (
        <div className="rounded-md bg-amber-50 p-3 dark:bg-amber-950/30">
          <p className="mb-1 text-sm font-medium text-amber-800 dark:text-amber-200">
            Could not match:
          </p>
          <div className="max-h-32 space-y-0.5 overflow-y-auto">
            {result.unmatched.map((u, i) => (
              <p key={i} className="text-xs text-amber-700 dark:text-amber-300">
                <strong>{u.filename}</strong>: {u.reason}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
